import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../header/Header";
import "../leave/Leave.css";
import moment from "moment";
import Axios from "axios";
import { toast } from "react-toastify";
import { verifyToken } from "../../helpers/signJwtToken";
import {
  LuAlertCircle,
  LuArrowRight,
  LuChevronLeft,
  LuChevronRight,
  LuCalendarDays,
  LuCalendarPlus,
  LuHistory,
  LuLoader2,
  LuPalmtree,
  LuSend,
} from "react-icons/lu";
import { UiButton, TEXTAREA } from "../ui/ui";
import { DateRangeFilter } from "../ui/DateFilter";
import SelectMenu from "../ui/SelectMenu";
import { Link } from "react-router-dom";
var queryString = require("query-string");
var config = require("../../config/config");

const MAX_PL = 21;
const MAX_CH = 2;
const HISTORY_PAGE_SIZE = 8;
// add this inside the Leave component (top-level, after hooks)

const Leave = () => {
  const [userDetails, setUserDetails] = useState(null);

  // form
  const [leaveType, setLeaveType] = useState(0); // 1=PL, 2=CH
  const [startDate, setStartDate] = useState(""); // PL only, YYYY-MM-DD
  const [endDate, setEndDate] = useState(""); // PL only, YYYY-MM-DD
  const [chHoliday, setChHoliday] = useState(0); // CH only (single date)
  const [comment, setComment] = useState("");

  // lists & balances
  const [opHolidayList, setOpHolidayList] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({
    plBalance: 0,
    chBalance: 0,
  });
  const [leaveHistory, setLeaveHistory] = useState([]);

  // ui guards
  const [btnBlock, setBtnBlock] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);

  // --- helpers
  const getUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const verified = await verifyToken(token);
    return verified?.payload?.sub || null;
  }, []);

  const fetchOptionalHolidayList = useCallback(async () => {
    try {
      const res = await Axios.get(
        config.API_BASE +
          "/calender/getHolidayList?" +
          queryString.stringify({
            type: 2,
            dateBetween:
              moment().format("YYYY") +
              "-01-01|" +
              moment().format("YYYY") +
              "-12-31",
          })
      );
      if (res.data?.status && Array.isArray(res.data?.data)) {
        setOpHolidayList(res.data.data);
      }
    } catch (e) {
      console.log("getOptionalHolidayList error:", e);
    }
  }, []);

  const fetchLeaveBalance = useCallback(async (u) => {
    if (!u?.id) return;
    try {
      const res = await Axios.get(
        config.API_BASE +
          "/leave/getUserLeaves?" +
          queryString.stringify({ userId: u.id })
      );
      if (
        res.data?.status &&
        Array.isArray(res.data?.data) &&
        res.data.data[0]
      ) {
        setLeaveBalance(res.data.data[0]);
      }
    } catch (e) {
      console.log("getUserLeaves error:", e);
    }
  }, []);

  const fetchLeaveHistory = useCallback(async (u) => {
    if (!u?.id) return;
    try {
      const res = await Axios.get(
        config.API_BASE +
          "/leave/getUserLeaveHistory?" +
          queryString.stringify({
            userId: u.id,
            dateBetween:
              moment().format("YYYY") +
              "-01-01|" +
              moment().format("YYYY") +
              "-12-31",
          })
      );
      if (res.data?.status && Array.isArray(res.data?.data)) {
        setLeaveHistory(res.data.data);
      }
    } catch (e) {
      console.log("getLeaveHistory error:", e);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const u = await getUser();
      setUserDetails(u);
      await Promise.all([
        fetchOptionalHolidayList(),
        fetchLeaveBalance(u),
        fetchLeaveHistory(u),
      ]);
    })();
  }, [getUser, fetchLeaveBalance, fetchLeaveHistory, fetchOptionalHolidayList]);

  // ----- derived
  const willDeduct = useMemo(() => {
    if (leaveType !== 1 || !startDate || !endDate) return 0;
    return moment(endDate).diff(startDate, "days") + 1;
  }, [leaveType, startDate, endDate]);

  // ----- validation
  const isValid = useCallback(() => {
    if (leaveType === 0) {
      toast.error("Please choose a leave type");
      return false;
    }
    if (leaveType === 1) {
      const isWeekend = (d) => [0, 6].includes(moment(d).day());
      if (startDate && moment(startDate).isBefore(moment(), "day")) {
        toast.error("Leave can't start in the past");
        return false;
      }
      if ((startDate && isWeekend(startDate)) || (endDate && isWeekend(endDate))) {
        toast.error("Leave can't start or end on a weekend");
        return false;
      }
      if (!startDate || !endDate) {
        toast.error("Please choose start and end dates");
        return false;
      }
      if (willDeduct <= 0) {
        toast.error("The end date must be on or after the start date");
        return false;
      }
      if (leaveBalance.plBalance < willDeduct) {
        toast.error("Not enough leave balance");
        return false;
      }
    }
    if (leaveType === 2) {
      if (!chHoliday || chHoliday === 0) {
        toast.error("Please choose an optional holiday");
        return false;
      }
      if (leaveBalance.chBalance < 1) {
        toast.error("Not enough leave balance");
        return false;
      }
    }
    return true;
  }, [leaveType, startDate, endDate, willDeduct, chHoliday, leaveBalance]);

  // ----- apply leave
  const onLeaveSubmit = useCallback(
    async (evt) => {
      evt.preventDefault();
      if (!userDetails?.id) return;
      // isValid() already explains what's missing
      if (!isValid()) return;
      setBtnBlock(true);
      try {
        const body = {
          userId: userDetails.id,
          leaveType, // 1=PL, 2=CH
          transactionType: 1, // deduction
          comment,
          noOfDays: 1,
        };
        if (leaveType === 1) {
          body.startDate = startDate;
          body.endDate = endDate;
          body.noOfDays = willDeduct;
        } else {
          body.startDate = chHoliday;
          body.endDate = chHoliday;
          body.noOfDays = 1;
        }

        const res = await Axios.post(
          config.API_BASE + "/leave/insertLeaveDetails",
          body
        );
        const data = res.data;
        if (data?.status) {
          toast.success("Leave applied");
          await Promise.all([
            fetchLeaveBalance(userDetails),
            fetchLeaveHistory(userDetails),
          ]);
          // reset form
          setLeaveType(0);
          setStartDate("");
          setEndDate("");
          setChHoliday(0);
          setComment("");
        } else {
          toast.error(data?.msg || "Unable to apply leave");
        }
      } catch (e) {
        console.log("insertLeaveDetails error:", e);
        toast.error("Something went wrong. Please try again.");
      } finally {
        setBtnBlock(false);
      }
    },
    [
      userDetails,
      isValid,
      leaveType,
      startDate,
      endDate,
      willDeduct,
      chHoliday,
      comment,
      fetchLeaveBalance,
      fetchLeaveHistory,
    ]
  );

  // ----- cancel upcoming leave
  const cancelLeave = useCallback(
    async ({ id, leaveType, startDate, endDate, noOfDays }) => {
      if (!userDetails?.id) return;
      setDeletingKey(id || `${leaveType}-${startDate}`);
      setBtnBlock(true);
      try {
        const res = await Axios.post(config.API_BASE + "/leave/cancelLeave", {
          userId: userDetails.id,
          leaveHistoryId: id, // optional, if you want to flag original row
          leaveType, // 1=PL, 2=CH
          startDate,
          endDate,
          noOfDays,
        });
        const data = res.data;
        if (data?.status) {
          toast.success("Leave cancelled");
          await Promise.all([
            fetchLeaveBalance(userDetails),
            fetchLeaveHistory(userDetails),
          ]);
        } else {
          toast.error(data?.msg || "Unable to cancel leave");
        }
      } catch (e) {
        console.log("cancelLeave error:", e);
        toast.error("Something went wrong. Please try again.");
      } finally {
        setBtnBlock(false);
        setDeletingKey(null);
      }
    },
    [userDetails, fetchLeaveBalance, fetchLeaveHistory]
  );

  // ----- history rows
  const historyRows = useMemo(() => {
    const today = moment().startOf("day");
    return leaveHistory.map((value, index) => {
      const key = value.id || `${value.leaveType}-${value.startDate}-${index}`;
      const start = moment(value.startDate).startOf("day");
      const noOfDays = Number(value.noOfDays || 1);
      const end = value.endDate
        ? moment(value.endDate).startOf("day")
        : moment(value.startDate)
            .add(noOfDays - 1, "days")
            .startOf("day");

      const isUpcoming = start.isSameOrAfter(today, "day");
      const isDeduction = String(value.transactionType) === "1"; // only cancel debits
      const alreadyCancelled =
        value.isCancelled === 1 ||
        value.isCancelled === true ||
        String(value.isCancelled) === "1";

      // Only allow cancel for upcoming deductions, not already-cancelled, and only PL/CH
      const cancelAllowed =
        !alreadyCancelled &&
        isUpcoming &&
        isDeduction &&
        (value.leaveType === 1 || value.leaveType === 2);

      const typeText =
        value.leaveType === 1
          ? "Paid Leave"
          : value.leaveType === 2
          ? "Optional Leave"
          : value.leaveType === 3
          ? "Paid Leave Deducted Due to Missed Marking Attendance"
          : value.leaveType === 4
          ? "Leave Without Pay"
          : "NA";

      const amount =
        value.transactionType === 1
          ? { text: "-" + (value.noOfDays || 1), tone: "debit" }
          : value.transactionType === 2
          ? { text: "+" + (value.noOfDays || 1), tone: "credit" }
          : { text: "NA", tone: "none" };

      return {
        key,
        value,
        start,
        end,
        noOfDays,
        isUpcoming,
        alreadyCancelled,
        cancelAllowed,
        typeText,
        amount,
      };
    });
  }, [leaveHistory]);

  const historyPageCount = Math.max(
    1,
    Math.ceil(historyRows.length / HISTORY_PAGE_SIZE),
  );
  const currentHistoryPage = Math.min(historyPage, historyPageCount);
  const pagedHistoryRows = historyRows.slice(
    (currentHistoryPage - 1) * HISTORY_PAGE_SIZE,
    currentHistoryPage * HISTORY_PAGE_SIZE,
  );

  // Only optional holidays that are still ahead, on a weekday, and not
  // already taken can be applied for.
  const takenOptionalDates = new Set(
    leaveHistory
      .filter(
        (l) =>
          Number(l.leaveType) === 2 &&
          String(l.transactionType) === "1" &&
          String(l.isCancelled) !== "1" &&
          l.isCancelled !== true,
      )
      .map((l) => moment(l.startDate).format("YYYY-MM-DD")),
  );
  const holidayOptions = opHolidayList
    .filter((h) => {
      const d = moment(h.date);
      return (
        d.isSameOrAfter(moment(), "day") &&
        ![0, 6].includes(d.day()) &&
        !takenOptionalDates.has(d.format("YYYY-MM-DD"))
      );
    })
    .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
    .map((h) => {
      const d = moment(h.date);
      return {
        value: h.date,
        label: h.holidayName,
        description: (() => {
          const away = d.diff(moment().startOf("day"), "days");
          const when = away === 0 ? "Today" : away === 1 ? "Tomorrow" : `In ${away} days`;
          return `${d.format("dddd, D MMMM")} · ${when}`;
        })(),
        leading: (
          <span className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-bg-amber-50 tw-text-amber-700">
            <span className="tw-text-sm tw-font-semibold tw-leading-4">{d.format("D")}</span>
            <span className="tw-text-[9px] tw-font-semibold tw-uppercase">{d.format("MMM")}</span>
          </span>
        ),
      };
    });

  const requestDays = leaveType === 1 ? willDeduct : leaveType === 2 && chHoliday ? 1 : 0;
  const currentBalance =
    leaveType === 1 ? leaveBalance.plBalance : leaveType === 2 ? leaveBalance.chBalance : 0;
  const balanceAfter = currentBalance - requestDays;


  return (
    <>
      <Header />
      <div className="container-fluid">
        <div className="tw-mx-auto tw-max-w-7xl tw-py-4 tw-font-sans tw-text-foreground sm:tw-py-6">
          <div className="tw-mb-6">
            <h1 className="tw-m-0 tw-text-2xl tw-font-semibold tw-leading-8 tw-text-foreground">
              Leave
            </h1>
            <div className="tw-mt-1 tw-text-sm tw-text-muted-foreground">
              Apply for leave and keep track of your balance for{" "}
              {moment().format("YYYY")}.
            </div>
          </div>

          {/* Balances */}
          <div className="tw-mb-6 tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <BalanceCard
              icon={LuCalendarDays}
              label="Paid leave"
              value={leaveBalance.plBalance}
              max={MAX_PL}
              tone="primary"
              description="For any working day. 21 days a year, credited at the start of each quarter."
            >
              <QuarterCredits />
            </BalanceCard>
            <BalanceCard
              icon={LuPalmtree}
              label="Optional leave"
              value={leaveBalance.chBalance}
              max={MAX_CH}
              tone="amber"
              description="Only for dates on this year's optional holiday list — pick the festivals that matter to you."
            >
              <Link
                to="/holiday-list"
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-primary tw-no-underline hover:tw-text-primary hover:tw-underline focus:tw-text-primary"
              >
                View optional holidays
                <LuArrowRight aria-hidden="true" />
              </Link>
            </BalanceCard>
          </div>

          <div className="tw-grid tw-items-start tw-gap-6 lg:tw-grid-cols-5">
            {/* Apply Leave */}
            <section className="tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-shadow-soft lg:tw-col-span-2">
              <CardHeading
                icon={LuCalendarPlus}
                title="Apply leave"
                subtitle="Pick your dates and check the balance before applying."
              />
              <form className="tw-space-y-5 tw-p-5 tw-pt-1" onSubmit={onLeaveSubmit}>
                <div>
                  <FieldLabel>Leave type</FieldLabel>
                  <div
                    className="tw-grid tw-grid-cols-2 tw-gap-1 tw-rounded-lg tw-bg-secondary tw-p-1"
                    role="radiogroup"
                    aria-label="Leave type"
                  >
                    {[
                      { value: 1, label: "Paid leave" },
                      { value: 2, label: "Optional leave" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={leaveType === opt.value}
                        onClick={() => setLeaveType(opt.value)}
                        className={`tw-h-9 tw-rounded-md tw-border-0 tw-text-sm tw-font-medium tw-transition-colors ${
                          leaveType === opt.value
                            ? "tw-bg-white tw-text-foreground tw-shadow-soft"
                            : "tw-bg-transparent tw-text-muted-foreground hover:tw-text-foreground"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div className="tw-mt-2 tw-text-xs tw-leading-5 tw-text-muted-foreground">
                    {leaveType === 1
                      ? "Paid leave can be taken on any working day and is deducted from your paid leave balance."
                      : leaveType === 2
                      ? "Optional leave can only be taken on one of this year's optional holidays."
                      : "Not sure which to pick? Paid leave works on any day; optional leave is only for optional holidays."}
                  </div>
                </div>

                {leaveType === 1 && (
                  <div>
                    <FieldLabel>Leave dates</FieldLabel>
                    <DateRangeFilter
                      fullWidth
                      label="Select start and end dates"
                      value={{ start: startDate, end: endDate }}
                      min={moment().format("YYYY-MM-DD")}
                      onChange={({ start, end }) => {
                        setStartDate(start || "");
                        setEndDate(end || "");
                      }}
                      onReset={() => {
                        setStartDate("");
                        setEndDate("");
                      }}
                    />
                    <div className="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">
                      Weekdays only — leave can't start or end on a weekend.
                    </div>
                  </div>
                )}

                {leaveType === 2 && (
                  <div>
                    <FieldLabel>Optional holiday</FieldLabel>
                    <SelectMenu
                      fullWidth
                      icon={LuPalmtree}
                      placeholder="Select an upcoming optional holiday"
                      emptyText="No upcoming optional holidays left this year."
                      value={chHoliday || ""}
                      onChange={setChHoliday}
                      options={holidayOptions}
                      ariaLabel="Optional holiday"
                    />
                  </div>
                )}

                {leaveType !== 0 && (
                  <div
                    className={`tw-grid tw-grid-cols-3 tw-divide-x tw-divide-border tw-rounded-lg tw-border tw-border-solid tw-text-center ${
                      balanceAfter < 0
                        ? "tw-border-red-200 tw-bg-red-50"
                        : "tw-border-border tw-bg-secondary/60"
                    }`}
                  >
                    <SummaryStat label="Balance" value={currentBalance} />
                    <SummaryStat label="This request" value={requestDays} />
                    <SummaryStat
                      label="After"
                      value={balanceAfter}
                      danger={balanceAfter < 0}
                    />
                  </div>
                )}
                {leaveType !== 0 && balanceAfter < 0 && (
                  <div className="-tw-mt-2 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-destructive">
                    <LuAlertCircle aria-hidden="true" />
                    Not enough balance for this request.
                  </div>
                )}

                <div>
                  <FieldLabel htmlFor="comment">
                    Comment{" "}
                    <span className="tw-font-normal tw-text-muted-foreground">
                      (optional)
                    </span>
                  </FieldLabel>
                  <textarea
                    id="comment"
                    rows={3}
                    className={TEXTAREA}
                    placeholder="Add a note for your manager"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>

                <UiButton
                  type="submit"
                  className="tw-w-full"
                  disabled={btnBlock || leaveType === 0}
                >
                  {btnBlock ? (
                    <LuLoader2 className="tw-animate-spin" aria-hidden="true" />
                  ) : (
                    <LuSend aria-hidden="true" />
                  )}
                  {btnBlock ? "Submitting…" : "Apply leave"}
                </UiButton>
              </form>
            </section>

            {/* History */}
            <section className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-shadow-soft lg:tw-col-span-3">
              <CardHeading
                icon={LuHistory}
                title="Leave history"
                subtitle={`All leave credits and debits in ${moment().format("YYYY")}`}
              />
              {historyRows.length === 0 ? (
                <div className="tw-mx-5 tw-mb-5 tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-px-4 tw-py-10 tw-text-center tw-text-sm tw-text-muted-foreground">
                  No leave recorded this year yet.
                </div>
              ) : (
                <div className="tw-overflow-x-auto">
                  <table className="tw-w-full tw-border-collapse tw-text-sm">
                    <thead>
                      <tr className="tw-border-0 tw-border-y tw-border-solid tw-border-border tw-bg-secondary/60 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
                        <th className="tw-whitespace-nowrap tw-px-5 tw-py-2.5 tw-font-semibold">Date</th>
                        <th className="tw-px-3 tw-py-2.5 tw-font-semibold">Type</th>
                        <th className="tw-px-3 tw-py-2.5 tw-text-right tw-font-semibold">Days</th>
                        <th className="tw-px-5 tw-py-2.5 tw-text-right tw-font-semibold">
                          <span className="tw-sr-only">Action</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedHistoryRows.map((row) => (
                        <tr
                          key={row.key}
                          className="tw-border-0 tw-border-b tw-border-solid tw-border-border last:tw-border-b-0 hover:tw-bg-secondary/40"
                        >
                          <td className="tw-whitespace-nowrap tw-px-5 tw-py-3 tw-align-top">
                            <div className="tw-font-medium tw-text-foreground">
                              {row.start.format("DD MMM YYYY")}
                            </div>
                            {row.amount.tone === "debit" &&
                              row.value.endDate &&
                              !row.end.isSame(row.start, "day") && (
                              <div className="tw-text-xs tw-text-muted-foreground">
                                to {row.end.format("DD MMM")}
                              </div>
                            )}
                          </td>
                          <td className="tw-px-3 tw-py-3 tw-align-top">
                            <div
                              className={`tw-leading-5 ${
                                row.alreadyCancelled
                                  ? "tw-text-muted-foreground tw-line-through"
                                  : "tw-text-foreground"
                              }`}
                            >
                              {row.typeText}
                            </div>
                            <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-1">
                              {row.alreadyCancelled && (
                                <span className="tw-rounded-full tw-bg-slate-100 tw-px-2 tw-py-px tw-text-[11px] tw-font-medium tw-text-slate-600">
                                  Cancelled
                                </span>
                              )}
                              {!row.alreadyCancelled &&
                                row.isUpcoming &&
                                row.amount.tone === "debit" && (
                                  <span className="tw-rounded-full tw-bg-primary/10 tw-px-2 tw-py-px tw-text-[11px] tw-font-medium tw-text-primary">
                                    Upcoming
                                  </span>
                                )}
                            </div>
                          </td>
                          <td className="tw-whitespace-nowrap tw-px-3 tw-py-3 tw-text-right tw-align-top">
                            <span
                              className={`tw-inline-flex tw-min-w-[36px] tw-justify-center tw-rounded-md tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-semibold ${
                                row.amount.tone === "credit"
                                  ? "tw-bg-emerald-50 tw-text-emerald-700"
                                  : row.amount.tone === "debit"
                                  ? "tw-bg-red-50 tw-text-red-700"
                                  : "tw-bg-secondary tw-text-muted-foreground"
                              }`}
                            >
                              {row.amount.text}
                            </span>
                          </td>
                          <td className="tw-whitespace-nowrap tw-px-5 tw-py-3 tw-text-right tw-align-top">
                            {row.cancelAllowed && (
                              <UiButton
                                variant="danger"
                                size="sm"
                                className="tw-text-destructive"
                                disabled={btnBlock}
                                title="Cancel this upcoming leave"
                                onClick={() =>
                                  cancelLeave({
                                    id: row.value.id,
                                    leaveType: row.value.leaveType,
                                    startDate: row.start.format("YYYY-MM-DD"),
                                    endDate: row.end.format("YYYY-MM-DD"),
                                    noOfDays: row.noOfDays,
                                  })
                                }
                              >
                                {deletingKey === row.key ? "Cancelling…" : "Cancel"}
                              </UiButton>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    page={currentHistoryPage}
                    pageCount={historyPageCount}
                    total={historyRows.length}
                    pageSize={HISTORY_PAGE_SIZE}
                    onChange={setHistoryPage}
                  />
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

// Page numbers with ellipses: 1 … 4 5 6 … 12
const pageItems = (page, pageCount) => {
  const pages = new Set([1, pageCount, page - 1, page, page + 1]);
  const sorted = [...pages]
    .filter((p) => p >= 1 && p <= pageCount)
    .sort((a, b) => a - b);
  const items = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) items.push(`gap-${p}`);
    items.push(p);
  });
  return items;
};

const Pagination = ({ page, pageCount, total, pageSize, onChange }) => {
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <nav
      className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-border tw-px-5 tw-py-3"
      aria-label="Leave history pages"
    >
      <div className="tw-text-xs tw-text-muted-foreground">
        Showing{" "}
        <span className="tw-font-semibold tw-text-foreground">
          {from}–{to}
        </span>{" "}
        of <span className="tw-font-semibold tw-text-foreground">{total}</span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1">
        <UiButton
          variant="outline"
          size="icon"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <LuChevronLeft aria-hidden="true" />
        </UiButton>
        {pageItems(page, pageCount).map((item) =>
          typeof item === "string" ? (
            <span
              key={item}
              className="tw-w-6 tw-text-center tw-text-xs tw-text-muted-foreground"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <UiButton
              key={item}
              variant={item === page ? "primary" : "ghost"}
              size="icon"
              onClick={() => onChange(item)}
              aria-label={`Page ${item}`}
              aria-current={item === page ? "page" : undefined}
              className="tw-text-xs"
            >
              {item}
            </UiButton>
          ),
        )}
        <UiButton
          variant="outline"
          size="icon"
          onClick={() => onChange(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          <LuChevronRight aria-hidden="true" />
        </UiButton>
      </div>
    </nav>
  );
};

const CardHeading = ({ icon: Icon, title, subtitle }) => (
  <div className="tw-flex tw-items-start tw-gap-3 tw-p-5">
    <div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 tw-text-base tw-text-primary">
      <Icon aria-hidden="true" />
    </div>
    <div className="tw-min-w-0">
      <h2 className="tw-m-0 tw-text-base tw-font-semibold tw-leading-6 tw-text-foreground">
        {title}
      </h2>
      <div className="tw-text-xs tw-text-muted-foreground">{subtitle}</div>
    </div>
  </div>
);

const FieldLabel = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    className="tw-m-0 tw-mb-1.5 tw-block tw-text-sm tw-font-medium tw-text-foreground"
  >
    {children}
  </label>
);

const SummaryStat = ({ label, value, danger = false }) => (
  <div className="tw-px-2 tw-py-2.5">
    <div className="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-muted-foreground">
      {label}
    </div>
    <div
      className={`tw-text-lg tw-font-semibold ${
        danger ? "tw-text-destructive" : "tw-text-foreground"
      }`}
    >
      {value}
    </div>
  </div>
);

const BALANCE_TONES = {
  primary: { tile: "tw-bg-primary/10 tw-text-primary", bar: "tw-bg-gradient-primary" },
  amber: { tile: "tw-bg-amber-100 tw-text-amber-700", bar: "tw-bg-amber-400" },
};

// Paid leave is credited at the start of each calendar quarter: 5 + 5 + 5 + 6 = 21
const QUARTER_CREDITS = [
  { label: "Q1", month: "Jan", days: 5 },
  { label: "Q2", month: "Apr", days: 5 },
  { label: "Q3", month: "Jul", days: 5 },
  { label: "Q4", month: "Oct", days: 6 },
];

const QuarterCredits = () => {
  const currentQuarter = moment().quarter(); // 1-4
  return (
    <div className="tw-grid tw-grid-cols-4 tw-gap-1.5">
      {QUARTER_CREDITS.map((q, i) => {
        const credited = i + 1 <= currentQuarter;
        const isCurrent = i + 1 === currentQuarter;
        return (
          <div
            key={q.label}
            className={`tw-rounded-md tw-px-2 tw-py-1.5 tw-text-center ${
              credited
                ? "tw-bg-primary/10 tw-text-primary"
                : "tw-bg-secondary tw-text-muted-foreground"
            } ${isCurrent ? "tw-ring-1 tw-ring-primary/40" : ""}`}
            title={`${q.label} (${q.month}): +${q.days} days${credited ? " — credited" : ""}`}
          >
            <div className="tw-text-sm tw-font-semibold tw-leading-5">+{q.days}</div>
            <div className="tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wide">
              {q.label} · {q.month}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const BalanceCard = ({ icon: Icon, label, value, max, tone, description, children }) => {
  const pct = max ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const styles = BALANCE_TONES[tone];
  return (
    <div className="tw-flex tw-flex-col tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-p-5 tw-shadow-soft">
      <div className="tw-flex tw-items-center tw-justify-between">
        <div className="tw-text-sm tw-font-medium tw-text-muted-foreground">
          {label}
        </div>
        <div
          className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg ${styles.tile}`}
        >
          <Icon aria-hidden="true" />
        </div>
      </div>
      <div className="tw-mt-2 tw-flex tw-items-baseline tw-justify-between tw-gap-2">
        <div className="tw-flex tw-items-baseline tw-gap-1.5">
          <span className="tw-text-3xl tw-font-semibold tw-leading-9 tw-text-foreground">
            {value}
          </span>
          <span className="tw-text-sm tw-text-muted-foreground">
            {Number(value) === 1 ? "day" : "days"} available
          </span>
        </div>
        <span className="tw-text-xs tw-text-muted-foreground">
          Max {max} / year
        </span>
      </div>
      <div className="tw-mt-3 tw-h-1.5 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-secondary">
        <div
          className={`tw-h-full tw-rounded-full ${styles.bar}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="tw-mt-3 tw-text-xs tw-leading-5 tw-text-muted-foreground">
        {description}
      </div>
      {children && <div className="tw-mt-3">{children}</div>}
    </div>
  );
};

export default Leave;
